import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus, ArrowRight, CalendarDays, Wallet, MapPin, Users, Trash2, CheckCircle2, Compass, X,
  Sparkles, Ticket, Image, Icon, Share2, Camera, KeyRound,
} from '../../components/ui/Icon';
import { getIataCode } from '../../utils/iata';
import { getDest, photoUrl } from '../../utils/destinations';
import { tripsApi } from '../../api/trips.api';
import { authApi } from '../../api/auth.api';
import type { Trip } from '../../types';
import Modal from '../../components/ui/Modal';
import ConfirmModal from '../../components/ui/ConfirmModal';
import NewTripWizard from '../../components/trips/newTrip/NewTripWizard';
import RedeemCodeModal from '../../components/agency/RedeemCodeModal';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { toast } from '../../components/ui/Toast';
import { useAuthStore } from '../../stores/auth.store';
import { getAccessToken, isAlreadyGone } from '../../api/client';
import PaywallModal from '../../components/billing/PaywallModal';
import ShareStoryModal from '../../components/trips/share/ShareStoryModal';
import TripCoverModal from '../../components/trips/cover/TripCoverModal';
import { PhotoCredit } from '../../components/ui/PhotoCredit';
import { format, differenceInDays } from 'date-fns';
import { tripMemberCount } from '../../utils/tripMembers';
import { ca, es, fr, it, enUS, type Locale } from 'date-fns/locale';
import { tripPermissions } from '../../utils/tripPermissions';
import FollowedTripsSection from './FollowedTripsSection';

const localeFor = (lang: string): Locale =>
  ({ ca, es, fr, it } as Record<string, Locale>)[lang] ?? enUS;

const PAST_PREVIEW = 4;

// ── Compact editorial section heading ───────────────────────────────────────
// Dot + mono kicker + counter pill + hairline divider (single row, no serif h2)
function SectionHead({
  kicker, count, dotColor = 'teal',
}: {
  kicker: string;
  count?: number;
  dotColor?: 'teal' | 'sand';
}) {
  const dotCls = dotColor === 'sand' ? 'bg-[#c68642]' : 'bg-teal-500';
  const pillCls = dotColor === 'sand'
    ? 'text-[#a86f33] dark:text-[#d09d68] bg-[#f4e8d4]/70 dark:bg-[#c68642]/15'
    : 'text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/30';
  const lineCls = dotColor === 'sand'
    ? 'bg-[#efe1cb] dark:bg-[#c68642]/20'
    : 'bg-slate-200 dark:bg-slate-800';
  const textCls = dotColor === 'sand'
    ? 'text-[#a86f33] dark:text-[#d09d68]'
    : 'text-slate-400 dark:text-slate-500';
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotCls}`} />
      <span className={`font-mono text-[10px] tracking-[0.16em] uppercase font-semibold whitespace-nowrap ${textCls}`}>
        {kicker}
      </span>
      {count != null && (
        <span className={`inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded font-mono text-[10px] font-bold tabular-nums ${pillCls}`}>
          {String(count).padStart(2, '0')}
        </span>
      )}
      <div className={`flex-1 h-px ${lineCls}`} />
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const [trips, setTrips]                   = useState<Trip[]>([]);
  const [loading, setLoading]               = useState(true);
  const [createOpen, setCreateOpen]         = useState(false);
  const [paywallOpen, setPaywallOpen]       = useState(false);
  const [showAllPast, setShowAllPast]       = useState(false);
  const [shareConfirmTrip, setShareConfirmTrip] = useState<Trip | null>(null);
  const [storyTrip, setStoryTrip]           = useState<Trip | null>(null);
  const [coverTrip, setCoverTrip]           = useState<Trip | null>(null);
  const [deleteTarget, setDeleteTarget]     = useState<Trip | null>(null);
  const [redeemOpen, setRedeemOpen]         = useState(false);
  const [deleting, setDeleting]             = useState(false);
  const [showWelcome, setShowWelcome]       = useState(() => !localStorage.getItem('gromly_welcomed'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, setAuth } = useAuthStore();
  const { t, i18n } = useTranslation();
  const deleteAction = (trip: Trip) => (tripPermissions(trip).isOwner ? () => setDeleteTarget(trip) : undefined);
  const coverAction = (trip: Trip) => (
    tripPermissions(trip).canEdit
      ? () => setCoverTrip(trip)
      : undefined
  );

  useEffect(() => {
    tripsApi.list()
      .then(setTrips)
      .catch(() => toast(t('dashboard.toast.loadFailed'), 'error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      toast(t('billing.payment.success'), 'success');
      const token = getAccessToken();
      if (token) authApi.me().then(u => setAuth(u, token));
      setSearchParams({}, { replace: true });
    } else if (payment === 'cancelled') {
      toast(t('billing.payment.cancelled'), 'info');
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openCreateOrPaywall() {
    if (user?.isAdmin || user?.isPro || (user?.tripCredits ?? 0) > 0) {
      setCreateOpen(true);
    } else {
      setPaywallOpen(true);
    }
  }

  function handleCreated(trip: Trip) {
    setTrips(prev => [trip, ...prev]);
    setCreateOpen(false);
    navigate(`/trips/${trip.id}/itinerary`);
  }

  async function handleDelete(tripId: string) {
    try {
      await tripsApi.delete(tripId);
    } catch (e) {
      if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
    }
    setTrips(prev => prev.filter(x => x.id !== tripId));
    toast(t('dashboard.toast.deleted'), 'success');
  }

  async function confirmDeleteTarget() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await handleDelete(deleteTarget.id);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  async function handleTogglePublish(tripId: string) {
    const result = await tripsApi.togglePublish(tripId);
    setTrips(prev => prev.map(t => t.id === tripId ? { ...t, ...result } : t));
    toast(t(result.isPublished ? 'dashboard.toast.published' : 'dashboard.toast.unpublished'), 'success');
  }

  const now      = new Date();
  const upcoming = trips
    .filter(t => new Date(t.endDate) >= now)
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  const past     = trips
    .filter(t => new Date(t.endDate) < now)
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  const dests    = new Set(trips.map(t => t.destination.split(',')[0].trim())).size;
  const totalNights = trips.reduce((sum, t) => sum + (t.nights ?? 0), 0);
  const budgetedTrips = trips.filter(t => t.budget != null && Number(t.budget) > 0);
  const avgBudget = budgetedTrips.length > 0
    ? Math.round(budgetedTrips.reduce((s, t) => s + Number(t.budget), 0) / budgetedTrips.length)
    : 0;

  const firstName = (user?.displayName || user?.username || '').split(' ')[0];
  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return t('dashboard.greeting.morning');
    if (h < 18) return t('dashboard.greeting.afternoon');
    return t('dashboard.greeting.evening');
  };
  const today = format(now, 'EEEE, MMMM d', { locale: localeFor(i18n.language) });
  const credits = user?.isAdmin || user?.isPro ? '∞' : (user?.tripCredits ?? 0);

  // Hero trip = first upcoming; the rest render as landscape cards below it
  const heroTrip = upcoming[0] ?? null;
  const restUpcoming = upcoming.slice(1);
  const visiblePast = showAllPast ? past : past.slice(0, PAST_PREVIEW);

  const summaryParts = [
    t('dashboard.summary.trips', { count: trips.length }),
    t('dashboard.summary.dests', { count: dests }),
    t('dashboard.summary.nights', { count: totalNights }),
    t('dashboard.summary.avg', { amount: avgBudget > 0 ? `€${avgBudget.toLocaleString(i18n.language)}` : '—' }),
    t('dashboard.summary.credits', { credits }),
  ];

  return (
    <div className="w-full min-h-full bg-slate-50 dark:bg-[#0a0f1a]">
      <div className="max-w-[1360px] mx-auto px-5 md:px-12">

        {/* ── Editorial header ────────────────────────────────────────── */}
        <header className="pt-6 md:pt-10 pb-7">
          <div className="flex items-end justify-between gap-6">
            <div className="flex-1 min-w-0">
              {/* Eyebrow greeting — mono uppercase con dot teal + contadores */}
              <div className="flex items-center gap-2.5 mb-3 flex-wrap">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />
                <span className="font-mono text-[10px] md:text-[11px] tracking-[0.16em] uppercase text-slate-400 dark:text-slate-500 font-semibold">
                  {greeting()}{firstName ? `, ${firstName}` : ''} · {today}
                </span>
                {trips.length > 0 && !loading && (
                  <span className="font-mono text-[10px] md:text-[11px] tracking-[0.12em] uppercase text-slate-400/60 dark:text-slate-500/60">
                    · {t('dashboard.eyebrow.trips', { count: trips.length })} · {t('dashboard.eyebrow.lived', { count: past.length })}
                  </span>
                )}
              </div>
              {/* Big serif heading — Fraunces opsz 144 */}
              <h1
                className="font-serif font-medium text-slate-900 dark:text-slate-100 leading-[1.02] mb-2.5"
                style={{ fontSize: 'clamp(2.25rem, 4.6vw, 3.75rem)', letterSpacing: '-0.03em' }}
              >
                {t('dashboard.title')}
              </h1>
              <p className="text-[14px] text-slate-500 dark:text-slate-400 max-w-xl leading-relaxed">
                {t('dashboard.tagline')}
              </p>
            </div>

            {/* New trip button — primary, top-right; beside it, the way into a trip an agency prepared (ADR-0033) */}
            <div className="shrink-0 hidden sm:flex items-center gap-2">
              <button
                onClick={() => setRedeemOpen(true)}
                className="flex items-center gap-2 h-11 px-4 rounded-2xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <Icon as={KeyRound} size={15} />
                {t('redeemCode.entry')}
              </button>
              <button
                onClick={openCreateOrPaywall}
                className="flex items-center gap-2 h-11 px-5 rounded-2xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-semibold hover:bg-slate-700 dark:hover:bg-white transition-colors shadow-md"
              >
                <Icon as={Plus} size={15} />
                {t('dashboard.newTrip')}
              </button>
            </div>
          </div>

          {/* Mobile new trip */}
          <div className="sm:hidden mt-4 flex items-center gap-2">
            <button
              onClick={openCreateOrPaywall}
              className="flex items-center gap-1.5 h-9 px-4 rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-semibold"
            >
              <Icon as={Plus} size={14} />
              {t('dashboard.newTripShort')}
            </button>
            <button
              onClick={() => setRedeemOpen(true)}
              className="flex items-center gap-1.5 h-9 px-4 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold"
            >
              <Icon as={KeyRound} size={14} />
              {t('redeemCode.entry')}
            </button>
          </div>
        </header>

        {/* ── Welcome banner ─────────────────────────────────────────────── */}
        {showWelcome && !loading && trips.some(t => t.isDemo) && (
          <div className="mb-10 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-2xl px-5 py-4 flex items-start gap-4">
            <div className="w-9 h-9 rounded-xl bg-teal-100 dark:bg-teal-800/50 flex items-center justify-center shrink-0 mt-0.5">
              <Icon as={MapPin} size={18} className="text-teal-600 dark:text-teal-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-teal-900 dark:text-teal-100 mb-0.5">{t('dashboard.welcome.title')}</p>
              <p className="text-sm text-teal-700 dark:text-teal-300">
                {t('dashboard.welcome.body')}
              </p>
              <div className="flex items-center gap-3 mt-3">
                <button
                  onClick={() => { const demo = trips.find(t => t.isDemo); if (demo) navigate(`/trips/${demo.id}/itinerary`); }}
                  className="text-xs font-semibold text-teal-700 dark:text-teal-300 hover:text-teal-900 dark:hover:text-teal-100 transition-colors cursor-pointer"
                >
                  {t('dashboard.welcome.explore')}
                </button>
                <button
                  onClick={openCreateOrPaywall}
                  className="text-xs font-semibold text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200 transition-colors cursor-pointer"
                >
                  {t('dashboard.welcome.create')}
                </button>
              </div>
            </div>
            <button
              onClick={() => { setShowWelcome(false); localStorage.setItem('gromly_welcomed', '1'); }}
              className="text-teal-400 dark:text-teal-600 hover:text-teal-600 dark:hover:text-teal-400 transition-colors cursor-pointer shrink-0"
            >
              <Icon as={X} size={16} />
            </button>
          </div>
        )}

        {/* ── Content ──────────────────────────────────────────────────── */}
        {loading ? (
          <LoadingScreen />
        ) : trips.length === 0 ? (
          <EmptyState onCreate={openCreateOrPaywall} />
        ) : (
          <div className="pb-4">

            {/* Próximos — split hero + landscape cards */}
            {heroTrip && (
              <section className="mb-12 md:mb-14">
                <SectionHead kicker={t('dashboard.sections.upcoming')} count={upcoming.length} />
                <SplitHeroCard
                  trip={heroTrip}
                  onOpen={() => navigate(`/trips/${heroTrip.id}/itinerary`)}
                  onDelete={deleteAction(heroTrip)}
                  onStory={() => setStoryTrip(heroTrip)}
                  onCover={coverAction(heroTrip)}
                />
                {restUpcoming.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mt-3.5">
                    {restUpcoming.map(trip => (
                      <LandscapeTripCard
                        key={trip.id}
                        trip={trip}
                        variant="upcoming"
                        onOpen={() => navigate(`/trips/${trip.id}/itinerary`)}
                        onDelete={deleteAction(trip)}
                        onShareClick={() => setShareConfirmTrip(trip)}
                        onStory={() => setStoryTrip(trip)}
                        onCover={coverAction(trip)}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Memory lane — landscape cards, collapsible */}
            {past.length > 0 && (
              <section className="mb-4">
                <SectionHead kicker={t('dashboard.sections.memoryLane')} count={past.length} dotColor="sand" />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5">
                  {visiblePast.map(trip => (
                    <LandscapeTripCard
                      key={trip.id}
                      trip={trip}
                      variant="past"
                      onOpen={() => navigate(`/trips/${trip.id}/itinerary`)}
                      onDelete={deleteAction(trip)}
                      onShareClick={() => setShareConfirmTrip(trip)}
                      onStory={() => setStoryTrip(trip)}
                      onCover={coverAction(trip)}
                    />
                  ))}
                </div>
                {past.length > PAST_PREVIEW && !showAllPast && (
                  <div className="text-center mt-5">
                    <button
                      onClick={() => setShowAllPast(true)}
                      className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.08em] uppercase text-teal-700 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-200 transition-colors cursor-pointer"
                    >
                      {t('dashboard.sections.seeAll', { count: past.length })}
                      <Icon as={ArrowRight} size={12} />
                    </button>
                  </div>
                )}
              </section>
            )}

          </div>
        )}

        {!loading && <FollowedTripsSection />}

        {/* ── Bottom summary strip ─────────────────────────────────────── */}
        {trips.length > 0 && !loading && (
          <div className="mt-6 mb-16 pt-4 border-t border-slate-200 dark:border-slate-800/80 flex items-center gap-4 flex-wrap">
            <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-slate-400/60 dark:text-slate-500/60">
              {t('dashboard.summary.label')}
            </span>
            <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500 tabular-nums">
              {summaryParts.join(' · ')}
            </span>
          </div>
        )}
      </div>

      {createOpen && <NewTripWizard onCreated={handleCreated} onClose={() => setCreateOpen(false)} />}
      <RedeemCodeModal open={redeemOpen} onClose={() => setRedeemOpen(false)} />

      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} />

      {/* ── Share / unshare confirmation modal ── */}
      {shareConfirmTrip && (
        <Modal
          open
          onClose={() => setShareConfirmTrip(null)}
          title={shareConfirmTrip.isPublished ? t('dashboard.community.removeTitle') : t('dashboard.community.shareTitle')}
          size="sm"
        >
          {shareConfirmTrip.isPublished ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                {t('dashboard.community.removeBody', { name: shareConfirmTrip.name })}
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShareConfirmTrip(null)}
                  className="btn-ghost"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => { handleTogglePublish(shareConfirmTrip.id); setShareConfirmTrip(null); }}
                  className="btn px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors cursor-pointer"
                >
                  {t('dashboard.community.remove')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                {t('dashboard.community.shareBody', { name: shareConfirmTrip.name })}
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShareConfirmTrip(null)}
                  className="btn-ghost"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => { handleTogglePublish(shareConfirmTrip.id); setShareConfirmTrip(null); }}
                  className="btn-primary px-4 py-2"
                >
                  {t('dashboard.community.publish')}
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {coverTrip && (
        <TripCoverModal
          trip={coverTrip}
          onClose={() => setCoverTrip(null)}
          onSaved={cover => {
            setTrips(prev => prev.map(x => x.id === coverTrip.id ? { ...x, ...cover } : x));
            setCoverTrip(null);
          }}
        />
      )}
      {storyTrip && (
        <ShareStoryModal trip={storyTrip} onClose={() => setStoryTrip(null)} />
      )}

      {/* ── Delete confirmation modal ── */}
      <ConfirmModal
        open={!!deleteTarget}
        title={t('dashboard.deleteTrip.title')}
        body={deleteTarget ? t('dashboard.deleteTrip.body', { name: deleteTarget.name }) : undefined}
        onConfirm={confirmDeleteTarget}
        onCancel={() => setDeleteTarget(null)}
        loading={deleting}
      />
    </div>
  );
}

// ── Split hero card — photo (left) + info panel (right) ─────────────────────
function SplitHeroCard({ trip, onOpen, onDelete, onStory, onCover }: {
  trip: Trip; onOpen: () => void; onStory: () => void;
  /** Absent unless you own the trip: deleting is the owner's (ADR-0033). */
  onDelete?: () => void;
  /** Absent when the viewer can't edit the trip. */
  onCover?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const loc        = localeFor(i18n.language);
  const dest       = getDest(trip.destination);
  const now        = new Date();
  const daysUntil  = differenceInDays(new Date(trip.startDate), now);
  const daysLeft   = differenceInDays(new Date(trip.endDate), now) + 1;
  const isActive   = new Date(trip.startDate) <= now && new Date(trip.endDate) >= now;
  const photoSrc   = trip.coverImageUrl || (dest.photo ? photoUrl(dest.photo, 1400) : '');
  const hasPhoto   = photoSrc !== '';
  const iata       = getIataCode(trip.destination);
  const participantCount = tripMemberCount(trip);

  const countLabel = isActive ? t('dashboard.hero.daysLeft') : t('dashboard.hero.departingIn');
  const countValue = isActive ? Math.max(daysLeft, 0) : Math.max(daysUntil, 0);
  const countUnit  = isActive
    ? t('dashboard.hero.left')
    : t(countValue === 1 ? 'dashboard.hero.day' : 'dashboard.hero.days');

  const datesShort = `${format(new Date(trip.startDate), 'MMM d', { locale: loc })} — ${format(new Date(trip.endDate), 'MMM d, yyyy', { locale: loc })}`;

  const fields = [
    { icon: MapPin,       label: t('dashboard.hero.fieldDestination'), value: trip.destination },
    { icon: CalendarDays, label: t('dashboard.hero.fieldDates'),       value: datesShort },
    { icon: Users,        label: t('dashboard.hero.fieldTravelers'),   value: t('dashboard.hero.people', { count: participantCount }) },
  ];

  return (
    <div
      className="grid md:grid-cols-[1.3fr_1fr] rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
      style={{ boxShadow: '0 32px 64px -32px rgba(15,23,42,0.35), 0 12px 32px -16px rgba(15,23,42,0.2)' }}
    >
      {/* Left — photo */}
      <div
        className="relative min-h-[280px] md:min-h-[420px] bg-[#0b1420] cursor-pointer group overflow-hidden"
        onClick={onOpen}
      >
        {hasPhoto ? (
          <img
            src={photoSrc}
            alt={trip.destination}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${dest.from}, ${dest.to})` }} />
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.05) 100%)' }}
        />

        {/* Top-left eyebrow */}
        <div className="absolute top-6 left-7 right-7 flex items-start justify-between gap-3 z-10">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-6 h-px bg-white/60" />
            <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-white/85 font-semibold whitespace-nowrap">
              {isActive ? t('dashboard.hero.onRoadNow') : t('dashboard.hero.nextAdventure')}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onCover && (
              <button
                onClick={e => { e.stopPropagation(); onCover(); }}
                className="w-8 h-8 rounded-full bg-black/35 hover:bg-white/25 backdrop-blur-md border border-white/15 text-white/85 hover:text-white flex items-center justify-center transition-all cursor-pointer"
                aria-label={t('trip.cover.changeAction')}
                title={t('trip.cover.changeAction')}
              >
                <Icon as={Camera} size={13} />
              </button>
            )}
            <button
              onClick={e => { e.stopPropagation(); onStory(); }}
              className="w-8 h-8 rounded-full bg-black/35 hover:bg-white/25 backdrop-blur-md border border-white/15 text-white/85 hover:text-white flex items-center justify-center transition-all cursor-pointer"
              aria-label={t('trip.story.entry')}
              title={t('trip.story.entry')}
            >
              <Icon as={Share2} size={13} />
            </button>
            {onDelete && (
              <button
                onClick={e => { e.stopPropagation(); onDelete(); }}
                className="w-8 h-8 rounded-full bg-black/35 hover:bg-red-500 backdrop-blur-md border border-white/15 text-white/85 hover:text-white flex items-center justify-center transition-all cursor-pointer"
                aria-label={t('dashboard.delete')}
              >
                <Icon as={Trash2} size={13} />
              </button>
            )}
          </div>
        </div>

        <PhotoCredit
          name={trip.coverImagePhotographer}
          url={trip.coverImagePhotographerUrl}
          className="absolute bottom-2.5 right-2.5 z-20 max-w-[60%]"
        />

        {/* Bottom block */}
        <div className="absolute left-7 right-7 bottom-7 z-10 pointer-events-none">
          <div className="flex flex-wrap items-center gap-2 mb-2.5">
            <span className="inline-flex items-center h-[22px] px-2 rounded bg-black/25 font-mono text-[10px] tracking-[0.14em] uppercase text-white/70 font-bold">
              {iata}
            </span>
            <span className="inline-flex items-center gap-1.5 h-[22px] px-2.5 rounded-full bg-teal-500 text-white font-mono text-[10px] tracking-[0.08em] uppercase font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white" />
              {isActive ? t('dashboard.hero.onRoad') : t('dashboard.hero.planning')}
            </span>
            {trip.isDemo && (
              <span className="inline-flex items-center h-[22px] px-2.5 rounded-full bg-amber-400/95 text-amber-900 font-mono text-[10px] tracking-[0.08em] uppercase font-bold">
                Demo
              </span>
            )}
          </div>
          <h2
            className="font-serif font-medium text-white leading-[1.0] mb-2.5 max-w-[16ch]"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', letterSpacing: '-0.02em', textShadow: '0 2px 18px rgba(0,0,0,0.25)' }}
          >
            {trip.name}
          </h2>
          <div className="flex items-center gap-2.5 font-mono text-[11px] tracking-[0.08em] uppercase text-white/75 flex-wrap">
            <span>{datesShort}</span>
            <span className="text-white/40">·</span>
            <span className="tabular-nums">{trip.nights}n</span>
            <span className="text-white/40">·</span>
            <span className="inline-flex items-center gap-1 tabular-nums"><Icon as={Users} size={11} />{participantCount}</span>
          </div>
        </div>
      </div>

      {/* Right — info panel */}
      <div className="p-7 md:p-8 flex flex-col gap-5">
        {/* Countdown */}
        <div className="pb-5 border-b border-slate-200 dark:border-slate-800">
          <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-slate-400 dark:text-slate-500 font-semibold mb-1">
            {countLabel}
          </div>
          <div className="flex items-baseline gap-2.5">
            <span
              className="font-serif font-medium text-slate-900 dark:text-slate-100 leading-none tabular-nums"
              style={{ fontSize: 'clamp(3.25rem, 6vw, 4.5rem)', letterSpacing: '-0.035em' }}
            >
              {countValue}
            </span>
            <span className="text-sm text-slate-400 dark:text-slate-500">{countUnit}</span>
          </div>
        </div>

        {/* Fields */}
        <div className="flex flex-col gap-3.5">
          {fields.map(({ icon, label, value }) => (
            <div key={label} className="flex items-start gap-3">
              <div className="text-slate-400 dark:text-slate-500 mt-0.5 shrink-0">
                <Icon as={icon} size={15} />
              </div>
              <div className="min-w-0">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 dark:text-slate-500 mb-0.5">
                  {label}
                </div>
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <button
          onClick={onOpen}
          className="mt-auto self-start flex items-center gap-2 h-11 px-5 rounded-2xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-semibold hover:bg-slate-700 dark:hover:bg-white transition-colors"
        >
          {t('dashboard.hero.openItinerary')}
          <Icon as={ArrowRight} size={15} />
        </button>
      </div>
    </div>
  );
}

// ── Landscape trip card (3:2) — used for secondary upcoming & memory lane ────
function LandscapeTripCard({ trip, variant, onOpen, onDelete, onShareClick, onStory, onCover }: {
  trip: Trip;
  variant: 'upcoming' | 'past';
  onOpen: () => void;
  /** Absent unless you own the trip: deleting is the owner's (ADR-0033). */
  onDelete?: () => void;
  onShareClick: () => void;
  onStory: () => void;
  /** Absent when the viewer can't edit the trip. */
  onCover?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const loc       = localeFor(i18n.language);
  const isPast    = variant === 'past';
  const dest      = getDest(trip.destination);
  const now       = new Date();
  const daysUntil = differenceInDays(new Date(trip.startDate), now);
  const isActive  = new Date(trip.startDate) <= now && new Date(trip.endDate) >= now;
  const photoSrc  = trip.coverImageUrl || (dest.photo ? photoUrl(dest.photo, 700) : '');
  const hasPhoto  = photoSrc !== '';
  const iata      = getIataCode(trip.destination);
  const participantCount = tripMemberCount(trip);

  // Top-right status pill (upcoming only)
  let pill: { text: string; cls: string } | null = null;
  if (!isPast) {
    if (isActive) {
      pill = { text: `● ${t('dashboard.card.active')}`, cls: 'bg-teal-500 text-white' };
    } else if (daysUntil >= 0) {
      pill = {
        text: daysUntil === 0 ? t('dashboard.today') : t('dashboard.card.inDays', { count: daysUntil }),
        cls: 'bg-black/35 backdrop-blur-sm text-white/90',
      };
    }
  }

  return (
    <div
      className="group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-xl shadow-md dark:shadow-black/40"
      style={{ aspectRatio: '3/2' }}
      onClick={onOpen}
    >
      {hasPhoto ? (
        <img
          src={photoSrc}
          alt={trip.destination}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${dest.from}, ${dest.to})` }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent pointer-events-none" />

      {/* Top row: IATA + status pill + hover actions */}
      <div className="absolute top-2.5 left-2.5 right-2.5 flex items-start justify-between gap-2 z-10">
        <span className="font-mono text-[10px] tracking-[0.14em] font-bold text-white/70 bg-black/25 px-2 py-0.5 rounded uppercase">
          {iata}
        </span>
        <div className="flex items-center gap-1.5">
          {pill && (
            <span className={`font-mono text-[10px] tracking-[0.08em] uppercase font-semibold px-2 py-0.5 rounded-full ${pill.cls}`}>
              {pill.text}
            </span>
          )}
          {/* Published indicator — upcoming cards only (past cards use the share toggle below) */}
          {!isPast && trip.isPublished && (
            <span className="flex items-center gap-0.5 font-mono text-[10px] tracking-[0.08em] uppercase font-semibold px-2 py-0.5 rounded-full bg-teal-500/85 text-white">
              <Icon as={Compass} size={10} />
            </span>
          )}
          {trip.isDemo && (
            <span className="font-mono text-[10px] tracking-[0.08em] uppercase font-bold px-2 py-0.5 rounded-full bg-amber-400/95 text-amber-900">
              Demo
            </span>
          )}
          {/* Published past trip: single always-visible toggle that doubles as the indicator */}
          {isPast && trip.isPublished && (
            <button
              onClick={e => { e.stopPropagation(); onShareClick(); }}
              className="w-7 h-7 rounded-full flex items-center justify-center bg-teal-500 text-white transition-colors cursor-pointer"
              aria-label={t('dashboard.community.shared')}
            >
              <Icon as={Compass} size={12} />
            </button>
          )}
          {/* Actions */}
          <div className="flex items-center gap-1">
            {isPast && !trip.isPublished && (
              <button
                onClick={e => { e.stopPropagation(); onShareClick(); }}
                className="w-7 h-7 rounded-full flex items-center justify-center bg-black/40 backdrop-blur-sm text-white/85 hover:bg-teal-500 hover:text-white transition-colors cursor-pointer"
                aria-label={t('dashboard.community.share')}
              >
                <Icon as={Compass} size={12} />
              </button>
            )}
            {onCover && (
              <button
                onClick={e => { e.stopPropagation(); onCover(); }}
                className="w-7 h-7 rounded-full bg-black/40 hover:bg-white/30 backdrop-blur-sm text-white/85 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
                aria-label={t('trip.cover.changeAction')}
                title={t('trip.cover.changeAction')}
              >
                <Icon as={Camera} size={12} />
              </button>
            )}
            <button
              onClick={e => { e.stopPropagation(); onStory(); }}
              className="w-7 h-7 rounded-full bg-black/40 hover:bg-white/30 backdrop-blur-sm text-white/85 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
              aria-label={t('trip.story.entry')}
              title={t('trip.story.entry')}
            >
              <Icon as={Share2} size={12} />
            </button>
            {onDelete && (
              <button
                onClick={e => { e.stopPropagation(); onDelete(); }}
                className="w-7 h-7 rounded-full bg-black/40 hover:bg-red-500 backdrop-blur-sm text-white/85 hover:text-white flex items-center justify-center transition-all cursor-pointer"
                aria-label={t('dashboard.delete')}
              >
                <Icon as={Trash2} size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bottom content */}
      <div className="absolute inset-x-0 bottom-0 p-3.5 md:p-4 z-10 pointer-events-none">
        <h3
          className="font-serif font-medium text-white leading-[1.1] mb-1.5 line-clamp-2"
          style={{ fontSize: 'clamp(1rem, 1.4vw, 1.25rem)', letterSpacing: '-0.02em', textShadow: '0 1px 14px rgba(0,0,0,0.3)' }}
        >
          {trip.name}
        </h3>
        <div className="flex items-center justify-between gap-2 font-mono text-[10px] text-white/60 tabular-nums">
          <span className="truncate">
            {format(new Date(trip.startDate), 'MMM d', { locale: loc })} – {format(new Date(trip.endDate), 'MMM d', { locale: loc })}
          </span>
          <span className="shrink-0">{trip.nights}n · {participantCount}p</span>
        </div>
        <PhotoCredit
          name={trip.coverImagePhotographer}
          url={trip.coverImagePhotographerUrl}
          className="block mt-1 pointer-events-auto"
        />
      </div>
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const features = t('dashboard.empty.features', { returnObjects: true }) as { label: string; desc: string }[];
  const featureIcons = [CalendarDays, MapPin, Sparkles, Ticket, Wallet, Users, CheckCircle2, Image];
  const colors = [
    'text-teal-500', 'text-violet-500', 'text-amber-500', 'text-sky-500',
    'text-orange-500', 'text-pink-500', 'text-emerald-500', 'text-rose-500',
  ];
  const bgs = [
    'bg-teal-50 dark:bg-teal-950/40',
    'bg-violet-50 dark:bg-violet-950/40',
    'bg-amber-50 dark:bg-amber-950/40',
    'bg-sky-50 dark:bg-sky-950/40',
    'bg-orange-50 dark:bg-orange-950/40',
    'bg-pink-50 dark:bg-pink-950/40',
    'bg-emerald-50 dark:bg-emerald-950/40',
    'bg-rose-50 dark:bg-rose-950/40',
  ];

  return (
    <div className="relative max-w-2xl mx-auto pt-4 pb-10">
      {/* Decorative aurora backdrop */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 -top-10 flex justify-center overflow-hidden">
        <div className="w-72 h-72 rounded-full bg-teal-300/30 dark:bg-teal-500/15 blur-3xl -translate-x-16" />
        <div className="w-72 h-72 rounded-full bg-violet-300/25 dark:bg-violet-500/15 blur-3xl translate-x-16 -ml-32" />
      </div>

      <div className="relative text-center">
        {/* Emblem with floating category chips */}
        <div className="relative mx-auto mb-6 w-20 h-20">
          <div className="absolute inset-0 rounded-[26px] bg-gradient-to-br from-teal-400 to-emerald-500 rotate-6 opacity-40" />
          <div className="relative w-20 h-20 rounded-[26px] bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-xl shadow-teal-500/30">
            <Icon as={Compass} size={36} className="text-white" />
          </div>
          <div className="absolute -top-2 -left-3 w-8 h-8 rounded-xl bg-white dark:bg-slate-800 shadow-md border border-black/5 dark:border-white/10 flex items-center justify-center animate-float">
            <Icon as={MapPin} size={15} className="text-violet-500" />
          </div>
          <div className="absolute -bottom-2 -right-3 w-8 h-8 rounded-xl bg-white dark:bg-slate-800 shadow-md border border-black/5 dark:border-white/10 flex items-center justify-center animate-float [animation-delay:1.2s]">
            <Icon as={CalendarDays} size={15} className="text-orange-500" />
          </div>
        </div>

        <p className="font-mono text-[10px] tracking-[0.2em] text-teal-500 dark:text-teal-400 uppercase mb-3">
          {t('dashboard.empty.kicker')}
        </p>
        <h2 className="font-serif font-medium text-4xl text-slate-900 dark:text-slate-100 mb-3">
          {t('dashboard.empty.title')}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-7 max-w-md mx-auto">
          {t('dashboard.empty.subtitle')}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button onClick={onCreate} className="btn-primary gap-2 shadow-md shadow-teal-500/20">
            <Icon as={Plus} size={16} /> {t('dashboard.empty.cta')}
          </button>
          <button
            onClick={() => navigate('/explore')}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <Icon as={Compass} size={16} /> {t('dashboard.empty.exploreCta')}
          </button>
        </div>
      </div>

      {/* Feature cards */}
      <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3 mt-12 text-left">
        {features.map(({ label, desc }, i) => (
          <div
            key={label}
            className="group rounded-2xl p-4 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/5 transition-all"
          >
            <div className={`w-9 h-9 rounded-xl ${bgs[i]} flex items-center justify-center mb-3`}>
              <Icon as={featureIcons[i]} size={17} className={colors[i]} />
            </div>
            <p className="text-[13px] font-semibold text-slate-800 dark:text-slate-200 mb-0.5">{label}</p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
